/**
 * TestRecapStrip — FLOW-SIMPLER-01 (Seth 2026-10-07: "the post-install
 * checklist, at least on the dispatch screen, is so bad… completely
 * confusing").
 *
 * ESC-era installs are handled manually, so the DISPATCH screen no longer
 * carries the close-out board at all. What remains is this one-line admin
 * test rig: email yourself the exact close-out recap built from this
 * dispatch's real ESC data. The real board lives on the Pegasus job's
 * Post-Install tab, where close-outs will actually run.
 */
import { useMemo, useState } from "react";
import { FlaskConical, Mail, RefreshCw } from "lucide-react";
import {
  usePostInstall, POST_INSTALL_DEFAULTS, useEmailRecapTest,
} from "./postInstall";
import { useCurrentUserEmail } from "@/features/inbox/api";
import { toastError } from "@/components/ui/mutationToast";

export function TestRecapStrip({ dispatch, custno, locno }: {
  dispatch: string;
  custno: string | null;
  locno: string | null;
}) {
  const key = useMemo(() => ({ dispatch, jobId: null }), [dispatch]);
  const { data } = usePostInstall(key);
  const recapTest = useEmailRecapTest();
  const myEmail = useCurrentUserEmail().data ?? null;
  const [note, setNote] = useState<string | null>(null);
  const row = data ?? { ...POST_INSTALL_DEFAULTS };

  if (!custno) return null;
  return (
    <div className="flex flex-wrap items-center gap-2.5 rounded-xl border border-dashed border-slate-300 bg-slate-50/70 px-3.5 py-2.5">
      <FlaskConical className="h-4 w-4 shrink-0 text-slate-400" />
      <span className="min-w-0 flex-1 text-xs leading-snug text-slate-500">
        <b className="text-slate-600">Close-out test rig</b> — ESC installs are handled manually; the real board
        lives on the Pegasus job. This emails <b className="text-slate-600">you</b> the recap built from this
        dispatch&rsquo;s real data.
      </span>
      <button type="button" disabled={recapTest.isPending || !myEmail}
        onClick={() =>
          recapTest.mutate(
            { key, custno, locno, reviewsEnabled: row.reviews_enabled, permitRequired: row.permit_required, myEmail: myEmail! },
            {
              onSuccess: (c) => setNote(`Sent — ${c.units} unit${c.units === 1 ? "" : "s"}, ${c.permits} permit${c.permits === 1 ? "" : "s"}, certificate ${c.certIncluded ? "included" : "pending"}.`),
              onError: (e) => toastError((e as Error).message),
            },
          )
        }
        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-hairline bg-white px-3 py-1.5 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-50">
        {recapTest.isPending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
        Email Me a Test
      </button>
      {note && <span className="w-full text-[11.5px] font-medium text-emerald-700">{note}</span>}
    </div>
  );
}
